import safeStorage from '@github-ui/safe-storage'
import {useCallback, useEffect, useRef, useState, type Dispatch} from 'react'

const safeLocalStorage = safeStorage('localStorage')

const USE_LOCAL_STORAGE_UPDATE_EVENT_NAME = 'local-storage-update'

class UseLocalStorageUpdateEvent extends Event {
  storageKey: string
  storageValue: unknown | undefined

  constructor(storageKey: string, storageValue: unknown | undefined) {
    super(USE_LOCAL_STORAGE_UPDATE_EVENT_NAME)
    this.storageKey = storageKey
    this.storageValue = storageValue
  }
}

declare global {
  interface DocumentEventMap {
    [USE_LOCAL_STORAGE_UPDATE_EVENT_NAME]: UseLocalStorageUpdateEvent
  }
}

/**
 * A hook that mirrors state to local storage.
 *
 * Syncs current storage value to state on mount & storage key change.
 * Emits & subscribes to window events to sync state across hook instances.
 */
export function useLocalStorage<T>(storageKey: string, fallbackState: T): readonly [T, Dispatch<T | undefined>] {
  // Ref to track latest fallbackState for use in callbacks/effects
  // Written in effect, read in callbacks - safe for React Compiler
  const fallbackStateRef = useRef(fallbackState)
  useEffect(() => {
    fallbackStateRef.current = fallbackState
  })

  const [value, setValue] = useState<T>(() => {
    const itemValue = safeLocalStorage.getItem(storageKey)
    if (itemValue) {
      return JSON.parse(itemValue)
    }
    // Use param directly - initializer only runs once when ref has same value
    return fallbackState
  })

  const setNextValue: Dispatch<T | undefined> = useCallback(
    nextValue => {
      setValue(nextValue !== undefined ? nextValue : fallbackStateRef.current)

      if (nextValue === undefined) {
        safeLocalStorage.removeItem(storageKey)
      } else {
        safeLocalStorage.setItem(storageKey, JSON.stringify(nextValue))
      }

      document.dispatchEvent(new UseLocalStorageUpdateEvent(storageKey, nextValue))
    },
    [storageKey],
  )

  /**
   * Subscribe to storage update events to continuously sync state
   */
  useEffect(() => {
    function handler(event: UseLocalStorageUpdateEvent) {
      if (event.storageKey === storageKey) {
        const storageValue = event.storageValue as T | undefined
        const nextValue: T = storageValue !== undefined ? storageValue : fallbackStateRef.current
        setValue(nextValue)
      }
    }

    document.addEventListener(USE_LOCAL_STORAGE_UPDATE_EVENT_NAME, handler)

    /**
     * During setup, it's _possible_ we've diverged, so we'll
     * immediately check for an update.
     *
     * This also provides a 'reset' in the event the storageKey was changed
     */
    const itemValue = safeLocalStorage.getItem(storageKey)
    if (itemValue) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional sync with external storage on mount/key change
      setValue(JSON.parse(itemValue))
    } else {
      setValue(fallbackStateRef.current)
    }

    return () => {
      document.removeEventListener(USE_LOCAL_STORAGE_UPDATE_EVENT_NAME, handler)
    }
  }, [storageKey])

  return [value, setNextValue]
}

function findLocalKeysWithPrefix(prefix: string): string[] {
  const keys = safeLocalStorage.getKeys()
  const keysWithPrefix: string[] = []
  for (const key of keys) {
    if (key?.startsWith(prefix)) {
      keysWithPrefix.push(key)
    }
  }
  return keysWithPrefix
}

export function clearLocalStorage(storageKeys: string[]) {
  for (const storageKey of storageKeys) {
    safeLocalStorage.removeItem(storageKey)
    document.dispatchEvent(new UseLocalStorageUpdateEvent(storageKey, undefined))
  }
}

export function getLocalStorageItemsForKeyPrefix(prefix: string): unknown[] {
  const values: unknown[] = []
  const keys = findLocalKeysWithPrefix(prefix)
  for (const key of keys) {
    const itemValue = safeLocalStorage.getItem(key)
    if (itemValue != null) {
      values.push(JSON.parse(itemValue))
    }
  }
  return values
}

export function clearLocalStorageByKeyPrefix(prefix: string) {
  const keysToClear = findLocalKeysWithPrefix(prefix)
  clearLocalStorage(keysToClear)
}
